/**
 * Achievements Process Handlers
 *
 * All achievement-related query handlers
 */

import { handleRequest } from './config'
import { ApiResponse } from '../types'
import { State } from '../../hooks/useAppState'
import { loadCloudFunctionsHandler } from '../handlers'
import { buildMockUserAchievementsView } from '../mocks/achievements'
import {
  UnseeAllAchievementsRequest,
  UnseeAllAchievementsResponse,
  UserAchievementsView
} from '../../../../pure-js/types/Achievement'
// import { validateAchievementData } from '../../features/gamer/achievements/helpers/achievementHelpers' // TODO: Uncomment when BE is ready

// ========================================
// QUERY HANDLERS
// ========================================

/**
 * Get user achievements for current gamer
 * Returns UserAchievementsView with all achievements, where they stand on each, unseen achievements, and totals
 */
export const getUserAchievements = async (state: State): Promise<ApiResponse<UserAchievementsView>> => {
  return handleRequest(
    async () => {
      // Mock data - nothing earned yet
      await new Promise((resolve) => setTimeout(resolve, 300))
      return {
        data: buildMockUserAchievementsView(state.user?.id || ''),
        success: true
      } as ApiResponse<UserAchievementsView>
    },
    async () => {
      try {
        const cloudFunctions = await loadCloudFunctionsHandler()
        const response = await cloudFunctions.getUserAchievements(state)

        if (!response.success) {
          console.warn('⚠️ [ProcessHandler] Cloud function call failed:', response.error)
          return {
            data: {} as UserAchievementsView,
            success: false,
            error: response.error || 'No user achievement data available'
          }
        }

        // Cloud function handler returns data directly as UserAchievementsView
        if (!response.data) {
          return {
            data: {} as UserAchievementsView,
            success: false,
            error: 'No user achievement data available'
          }
        }

        return {
          data: response.data,
          success: true
        }
      } catch (error: unknown) {
        console.error('❌ [ProcessHandler] Error fetching user achievements:', error)
        const message = error instanceof Error ? error.message : 'Failed to fetch user achievements'
        return {
          data: {} as UserAchievementsView,
          success: false,
          error: message
        }
      }
    }
  )
}

/**
 * Mark achievements as seen
 * Clears unseen achievements from user's achievement document
 */
export const markAchievementsSeen = async (
  state: State,
  achievementIds?: string[]
): Promise<ApiResponse<{ success: boolean; clearedCount: number }>> => {
  return handleRequest(
    async () => {
      // Mock data
      await new Promise((resolve) => setTimeout(resolve, 200))
      return {
        data: {
          success: true,
          clearedCount: achievementIds?.length || 0
        },
        success: true
      } as ApiResponse<{ success: boolean; clearedCount: number }>
    },
    async () => {
      try {
        const cloudFunctions = await loadCloudFunctionsHandler()
        const response = await cloudFunctions.markAchievementsSeen(state, achievementIds)

        if (!response.success || !response.data) {
          console.warn('⚠️ [ProcessHandler] Failed to mark achievements as seen')
          return {
            data: { success: false, clearedCount: 0 },
            success: false,
            error: 'Failed to mark achievements as seen'
          }
        }

        const data = response.data as { success: boolean; clearedCount: number }

        return {
          data,
          success: true
        }
      } catch (error: unknown) {
        console.error('❌ [ProcessHandler] Error marking achievements as seen:', error)
        const message = error instanceof Error ? error.message : 'Failed to mark achievements as seen'
        return {
          data: { success: false, clearedCount: 0 },
          success: false,
          error: message
        }
      }
    }
  )
}

// ========================================
// DEV / ADMIN
// ========================================

export const unseeAllAchievements = async (
  state: State,
  req: UnseeAllAchievementsRequest
): Promise<ApiResponse<UnseeAllAchievementsResponse>> => {
  return handleRequest(
    async () => {
      await new Promise((resolve) => setTimeout(resolve, 200))
      return {
        data: {
          success: true,
          userId: req.userId || state.user?.id || '',
          unseenCount: 0
        },
        success: true
      } as ApiResponse<UnseeAllAchievementsResponse>
    },
    async () => {
      const cloudFunctions = await loadCloudFunctionsHandler()
      return cloudFunctions.unseeAllAchievements(req, state)
    }
  )
}
